import React, { useState, useEffect } from 'react';
import { useModal } from '../../hooks/useModal';
import Swal from 'sweetalert2';
import { productService, referenceService, warehouseService, brandService } from '../../services/api';
import { cleanText } from '../../utils/textHelpers';

const StockManagement = ({ user }) => {
  const handleSaveWarehouse = async (e) => {
    e.preventDefault();
    try {
      if (selectedWarehouse) {
        await warehouseService.update(selectedWarehouse.id, warehouseData);
        Swal.fire('Success', 'Warehouse updated successfully', 'success');
      } else {
        await warehouseService.create(warehouseData);
        Swal.fire('Success', 'Warehouse created successfully', 'success');
      }
      setShowWarehouseModal(false);
      loadWarehouses();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || 'Failed to save warehouse', 'error');
    }
  };

  const handleEditWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setWarehouseData({ name: warehouse.name, location: warehouse.location, description: warehouse.description });
    setShowWarehouseModal(true);
  };

  const handleDeleteWarehouse = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await warehouseService.delete(id);
        Swal.fire('Deleted!', 'Warehouse has been deleted.', 'success');
        loadWarehouses();
      } catch (error) {
        Swal.fire('Error', error.response?.data?.error || 'Failed to delete warehouse', 'error');
      }
    }
  };

  const canManageStock = ['Administrator', 'Manager', 'Employee'].includes(user?.role);
  const canDelete = ['Administrator', 'Manager'].includes(user?.role);
  // Auditor is read-only implicitly if not in canManageStock
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState([]);
  const [stockTransfers, setStockTransfers] = useState([]);
  const [stockOuts, setStockOuts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedStockOut, setSelectedStockOut] = useState(null);
  const [showAllLowStock, setShowAllLowStock] = useState(false);

  // Warehouse State
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseData, setWarehouseData] = useState({ name: '', location: '', description: '' });

  // Brand State
  const [brands, setBrands] = useState([]);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandData, setBrandData] = useState({ name: '', description: '' });

  useEffect(() => {
    loadWarehouses();
    loadProducts();
    loadProjects();
    loadBrands();
  }, []);

  const [searchTerm, setSearchTerm] = useState(''); // Added search state

  useEffect(() => {
    if (activeTab === 'shipments') loadShipments();
    if (activeTab === 'transfers') loadTransfers();
    if (activeTab === 'stockout') loadStockOuts();
  }, [activeTab]);

  const loadBrands = async () => {
    try {
      const response = await brandService.getAll();
      setBrands(response.data);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await warehouseService.getAll();
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getAll();
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setLoading(false);
    }
  };

  const loadShipments = async () => {
    try {
      const res = await productService.getShipments();
      setShipments(res.data);
    } catch (err) {
      console.error("Failed to load shipments", err);
    }
  };

  const loadTransfers = async () => {
    try {
      const res = await productService.getTransfers();
      setStockTransfers(res.data);
    } catch (err) {
      console.error("Failed to load transfers", err);
    }
  };

  const loadStockOuts = async () => {
    try {
      const res = await productService.getStockOuts();
      setStockOuts(res.data);
    } catch (err) {
      console.error("Failed to load stock outs", err);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await referenceService.getProjects();
      setProjects(res.data);
    } catch (err) {
      console.error("Failed to load projects", err);
    }
  };



  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [viewType, setViewType] = useState('');
  const [showStockOutModal, setShowStockOutModal] = useState(false);

  // Hide navbar when any modal is open
  useModal(showProductModal || showShipmentModal || showTransferModal || showViewModal || showStockOutModal || showWarehouseModal || showBrandModal, () => {
    setShowProductModal(false);
    setShowShipmentModal(false);
    setShowTransferModal(false);
    setShowViewModal(false);
    setShowStockOutModal(false);
    setShowWarehouseModal(false);
    setShowBrandModal(false);
  });

  const [stockOutData, setStockOutData] = useState({
    projectId: '',
    product: '',
    quantity: '',
    warehouse: ''
  });

  // Form data for Products
  const [productData, setProductData] = useState({
    modelNumber: '',
    name: '',
    modelNumber: '',
    name: '',
    brand: '',
    BrandId: null,
    price: '',
    description: '',
    warehouse1: '',
    warehouse2: ''
  });

  // Dynamic Stock State
  const [stockEntries, setStockEntries] = useState([{ warehouseId: '', quantity: '' }]);

  const handleAddStockEntry = () => {
    setStockEntries([...stockEntries, { warehouseId: '', quantity: '' }]);
  };

  const handleRemoveStockEntry = (index) => {
    const list = [...stockEntries];
    list.splice(index, 1);
    setStockEntries(list);
  };

  const handleStockEntryChange = (index, field, value) => {
    const list = [...stockEntries];
    list[index][field] = value;
    setStockEntries(list);
  };

  const [shipmentData, setShipmentData] = useState({
    supplier: '',
    date: '',
    warehouse: '',
    shipmentNo: '',
    items: [{ product: '', quantity: '' }]
  });

  const handleAddShipmentItem = () => {
    setShipmentData({
      ...shipmentData,
      items: [...shipmentData.items, { product: '', quantity: '' }]
    });
  };

  const handleRemoveShipmentItem = (index) => {
    const newItems = [...shipmentData.items];
    newItems.splice(index, 1);
    setShipmentData({ ...shipmentData, items: newItems });
  };

  const handleShipmentItemChange = (index, field, value) => {
    const newItems = [...shipmentData.items];
    newItems[index][field] = value;
    setShipmentData({ ...shipmentData, items: newItems });
  };

  const [transferData, setTransferData] = useState({
    product: '',
    fromWarehouse: '',
    toWarehouse: '',
    quantity: '',
    date: ''
  });



  const lowStockThreshold = 50;

  // Handle Add/Edit Product
  /* --- Brand Handlers --- */
  const handleSaveBrand = async (e) => {
    e.preventDefault();
    try {
      if (selectedBrand) {
        await brandService.update(selectedBrand.id, brandData);
        Swal.fire('Success', 'Brand updated successfully', 'success');
      } else {
        await brandService.create(brandData);
        Swal.fire('Success', 'Brand created successfully', 'success');
      }
      setShowBrandModal(false);
      loadBrands();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || 'Failed to save brand', 'error');
    }
  };

  const handleEditBrand = (brand) => {
    setSelectedBrand(brand);
    setBrandData({ name: brand.name, description: brand.description });
    setShowBrandModal(true);
  };

  const handleDeleteBrand = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await brandService.delete(id);
        Swal.fire('Deleted!', 'Brand has been deleted.', 'success');
        loadBrands();
      } catch (error) {
        Swal.fire('Error', error.response?.data?.error || 'Failed to delete brand', 'error');
      }
    }
  };

  const [errors, setErrors] = useState({});

  const validateProductForm = () => {
    const newErrors = {};
    if (!productData.name) newErrors.name = 'Product Name is required';
    if (!productData.modelNumber) newErrors.modelNumber = 'Model Number is required';
    if (!productData.brand) newErrors.brand = 'Brand is required';
    if (!productData.price) newErrors.price = 'Price is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!validateProductForm()) {
      Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    // Prepare payload
    const payload = {
      modelNumber: productData.modelNumber, // No auto-generation
      name: productData.name,
      brand: productData.brand,
      BrandId: productData.BrandId,
      price: parseFloat(productData.price),
      description: productData.description,
      stocks: stockEntries
    };

    try {
      if (selectedProduct) {
        // Update functionality
        await productService.update(selectedProduct.id, payload);
        Swal.fire('Success', 'Product updated successfully!', 'success');
      } else {
        // Create functionality
        await productService.create(payload);
        Swal.fire('Success', 'Product added successfully!', 'success');
      }

      setShowProductModal(false);
      setProductData({ modelNumber: '', name: '', brand: '', price: '', description: '', warehouse1: '', warehouse2: '' });
      setStockEntries([{ warehouseId: '', quantity: '' }]); // Reset stock entries
      setErrors({});
      setSelectedProduct(null); // Reset selection
      loadProducts();
    } catch (error) {
      console.error(error);
      // setShowProductModal(false); // Keep open on error
      Swal.fire('Error', error.response?.data?.error || `Failed to ${selectedProduct ? 'update' : 'add'} product`, 'error');
    }
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setProductData({
      modelNumber: product.modelNumber,
      name: product.name,
      brand: product.brand,
      BrandId: product.BrandId,
      price: product.price,
      description: product.description || '',
      // warehouse1: product.warehouse1,
      // warehouse2: product.warehouse2
    });

    if (product.ProductStocks && product.ProductStocks.length > 0) {
      const entries = product.ProductStocks.map(s => ({
        warehouseId: s.WarehouseId,
        quantity: s.quantity
      }));
      setStockEntries(entries);
    } else {
      setStockEntries([{ warehouseId: '', quantity: '' }]);
    }

    setShowProductModal(true);
  };

  const handleDeleteProduct = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await productService.delete(id);
        Swal.fire('Deleted!', 'Product has been deleted.', 'success');
        loadProducts();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Cannot Delete Product',
          text: error.response?.data?.error || 'Failed to delete product. It may be in use.',
          confirmButtonColor: '#d33'
        });
      }
    }
  };

  // Handle Add/Edit Shipment
  // Handle Add/Edit Shipment
  const handleAddShipment = async (e) => {
    e.preventDefault();
    const payload = {
      shipmentNo: shipmentData.shipmentNo || getNextShipmentNumber(),
      supplier: shipmentData.supplier,
      date: shipmentData.date,
      warehouse: shipmentData.warehouse, // Send ID
      status: 'Received',
      items: shipmentData.items // Send items array for create
    };

    try {
      if (selectedShipment) {
        // Multi-Item Edit Logic
        // 1. Get all original items for this shipmentNo
        const originalItems = shipments.filter(s => s.shipmentNo === selectedShipment.shipmentNo);
        const originalIds = new Set(originalItems.map(s => s.id));
        const currentIds = new Set();

        // 2. Process form items
        for (const item of shipmentData.items) {
          const itemPayload = {
            shipmentNo: payload.shipmentNo,
            supplier: payload.supplier,
            date: payload.date,
            warehouse: payload.warehouse,
            status: payload.status,
            location: warehouses.find(w => w.id == payload.warehouse)?.name, // Ensure location name is sent if needed
            product: parseInt(item.product),
            quantity: parseInt(item.quantity)
          };

          if (item.id) {
            // Update Existing Item
            currentIds.add(item.id);
            // We need to pass 'warehouse' ID if backend expects it to update location/stock
            // Our backend update logic might need 'warehouse' in body.
            await productService.updateShipment(item.id, itemPayload);
          } else {
            // Create New Item in existing shipment group
            await productService.createShipment(itemPayload);
          }
        }

        // 3. Delete removed items
        for (const id of originalIds) {
          if (!currentIds.has(id)) {
            await productService.deleteShipment(id);
          }
        }
        Swal.fire('Success', 'Shipment updated!', 'success');
      } else {
        await productService.createShipment(payload);
        Swal.fire('Success', 'Shipment recorded successfully!', 'success');
      }
      setShowShipmentModal(false);
      setShipmentData({ supplier: '', date: '', warehouse: '', shipmentNo: '', items: [{ product: '', quantity: '' }] });
      setSelectedShipment(null);
      loadProducts();
      loadShipments();
    } catch (error) {
      console.error("Shipment Save Error:", error);
      setShowShipmentModal(false);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to save shipment';
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const [selectedShipment, setSelectedShipment] = useState(null);
  const handleEditShipment = (s) => {
    setSelectedShipment(s);
    // Find warehouse ID by name (s.location)
    const wh = warehouses.find(w => w.name === s.location);

    // Find ALL items in this shipment group
    const groupItems = shipments.filter(item => item.shipmentNo === s.shipmentNo);

    setShipmentData({
      shipmentNo: s.shipmentNo,
      supplier: s.supplier,
      date: s.date,
      warehouse: wh ? wh.id : '',
      items: groupItems.map(item => ({
        id: item.id, // Track ID for updates
        product: item.Product ? item.Product.id : (item.ProductId || item.product),
        quantity: item.quantity
      }))
    });
    setShowShipmentModal(true);
  };

  const handleDeleteShipment = async (id) => {
    const result = await Swal.fire({ title: 'Delete Shipment?', text: 'Stock will NOT be automatically reverted in this version.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (result.isConfirmed) {
      try {
        await productService.deleteShipment(id); // Need to add to API
        Swal.fire('Deleted', 'Shipment deleted', 'success');
        loadShipments();
      } catch (e) { console.error(e); }
    }
  };

  // Handle Add Transfer
  const handleAddTransfer = async (e) => {
    e.preventDefault();
    const payload = {
      transferNo: getNextTransferNumber(),
      product: parseInt(transferData.product),
      quantity: parseInt(transferData.quantity),
      fromWarehouse: transferData.fromWarehouse,
      toWarehouse: transferData.toWarehouse,
      date: transferData.date,
      status: 'Completed'
    };

    if (payload.fromWarehouse === payload.toWarehouse) {
      Swal.fire('Error', 'Source and destination warehouses cannot be the same', 'error');
      return;
    }

    try {
      if (selectedTransfer) {
        await productService.updateTransfer(selectedTransfer.id, payload);
        Swal.fire('Success', 'Stock transfer updated!', 'success');
      } else {
        await productService.createTransfer(payload);
        Swal.fire('Success', 'Stock transfer completed successfully!', 'success');
      }
      setShowTransferModal(false);
      setTransferData({ product: '', fromWarehouse: '', toWarehouse: '', quantity: '', date: '' });
      setSelectedTransfer(null);
      loadProducts();
      loadTransfers();
    } catch (error) {
      console.error(error);
      setShowTransferModal(false); // Close on error
      Swal.fire('Error', error.response?.data?.error || 'Failed to transfer stock', 'error');
    }
  };

  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const handleEditTransfer = (t) => {
    setSelectedTransfer(t);
    const fromWh = warehouses.find(w => w.name === t.from);
    const toWh = warehouses.find(w => w.name === t.to);
    setTransferData({
      transferNo: t.transferNo,
      product: t.Product ? t.Product.id : (t.ProductId || t.product),
      quantity: t.quantity,
      fromWarehouse: fromWh ? fromWh.id : '',
      toWarehouse: toWh ? toWh.id : '',
      date: t.date
    });
    setShowTransferModal(true);
  };

  const handleDeleteTransfer = async (id) => {
    const result = await Swal.fire({ title: 'Delete Transfer?', text: 'Stock will NOT be automatically reverted in this version.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (result.isConfirmed) {
      try {
        await productService.deleteTransfer(id);
        Swal.fire('Deleted', 'Transfer deleted', 'success');
        loadTransfers();
      } catch (e) { console.error(e); }
    }
  };

  const handleEditStockOut = (s) => {
    setSelectedStockOut(s);
    setStockOutData({
      projectId: s.ProjectId,
      product: s.ProductId,
      quantity: s.quantity,
      warehouse: s.fromWarehouse
    });
    setShowStockOutModal(true);
  };

  const handleStockOut = async (e) => {
    e.preventDefault();
    try {
      if (!stockOutData.projectId) { // Use projectId from state
        Swal.fire('Error', 'Please select a project', 'error');
        return;
      }
      const payload = {
        ...stockOutData,
        project: stockOutData.projectId,
        product: stockOutData.product,
        fromWarehouse: stockOutData.warehouse // Send ID
      };

      if (selectedStockOut) {
        await productService.updateStockOut(selectedStockOut.id, payload);
        Swal.fire('Success', 'Stock issuance updated', 'success');
      } else {
        await productService.createStockOut(payload);
        Swal.fire('Success', 'Stock issued successfully', 'success');
      }

      setShowStockOutModal(false);
      setStockOutData({ projectId: '', product: '', quantity: '', warehouse: '' });
      setSelectedStockOut(null);
      loadStockOuts();
      loadProducts();
    } catch (error) {
      console.error("Stock save error:", error.response ? error.response.data : error);
      setShowStockOutModal(false); // Close on error
      Swal.fire('Error', 'Failed to save stock record: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const getNextModelNumber = () => {
    return 'PRD-' + Math.floor(1000 + Math.random() * 9000);
  };

  const getNextShipmentNumber = () => {
    return 'SHP-' + Date.now().toString().slice(-6);
  };

  const getNextTransferNumber = () => {
    return 'TRF-' + Date.now().toString().slice(-6);
  };

  const handleDeleteStockOut = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await productService.deleteStockOut(id);
        Swal.fire('Deleted!', 'Stock issuance has been deleted.', 'success');
        loadStockOuts();
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Failed to delete stock issuance', 'error');
      }
    }
  };

  const lowStockProducts = products.filter(p => {
    if (p.ProductStocks && p.ProductStocks.length > 0) {
      return p.ProductStocks.some(s => s.quantity < lowStockThreshold);
    }
    return p.totalStock < lowStockThreshold;
  });

  // Filter Logic
  const filteredProducts = products.filter(p =>
    !searchTerm ||
    (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.modelNumber && p.modelNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredShipments = shipments.filter(s =>
    !searchTerm ||
    (s.shipmentNo && s.shipmentNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.supplier && s.supplier.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.Product && s.Product.modelNumber && s.Product.modelNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.Product && s.Product.name && s.Product.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredTransfers = stockTransfers.filter(t =>
    !searchTerm ||
    (t.transferNo && t.transferNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.Product && t.Product.modelNumber && t.Product.modelNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.Product && t.Product.name && t.Product.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredStockOuts = stockOuts.filter(s =>
    !searchTerm ||
    (s.Project && s.Project.title && s.Project.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.Product && s.Product.modelNumber && s.Product.modelNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.Product && s.Product.name && s.Product.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Stock & Inventory Management
          </h2>
          <p className="text-muted mb-0">Manage your inventory and stock levels</p>
        </div>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => { setActiveTab('products'); setSearchTerm(''); }}
          >
            Products
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'shipments' ? 'active' : ''}`}
            onClick={() => { setActiveTab('shipments'); setSearchTerm(''); }}
          >
            Shipments
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'transfers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('transfers'); setSearchTerm(''); }}
          >
            Stock Transfers
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'stockout' ? 'active' : ''}`}
            onClick={() => { setActiveTab('stockout'); setSearchTerm(''); }}
          >
            Stock Out
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'warehouses' ? 'active' : ''}`}
            onClick={() => { setActiveTab('warehouses'); setSearchTerm(''); }}
          >
            Warehouses
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'brands' ? 'active' : ''}`}
            onClick={() => { setActiveTab('brands'); setSearchTerm(''); }}
          >
            Brands
          </button>
        </li>
      </ul>

      {
        activeTab === 'products' && (
          <div>
            {lowStockProducts.length > 0 && (
              <div className="alert alert-warning mb-3">
                <i className="bi bi-exclamation-triangle-fill"></i> <strong>Low Stock Alert:</strong> {lowStockProducts.length} product(s) have stock below {lowStockThreshold} units
                <ul className="mb-0 mt-2">
                  {(showAllLowStock ? lowStockProducts : lowStockProducts.slice(0, 5)).map(p => (
                    <li key={p.id}>
                      {p.name} ({p.modelNumber}):
                      {p.ProductStocks && p.ProductStocks.length > 0 ? (
                        p.ProductStocks.map((s, i) => (
                          <span key={s.id}>
                            {i > 0 && ", "} {s.Warehouse ? s.Warehouse.name : 'Unknown'}: <span className={s.quantity < lowStockThreshold ? "text-danger" : ""}>{s.quantity}</span>
                          </span>
                        ))
                      ) : (
                        <span className="text-danger"> Out of Stock</span>
                      )}
                    </li>
                  ))}
                </ul>
                {lowStockProducts.length > 5 && (
                  <button
                    className="btn btn-link btn-sm p-0 mt-2"
                    onClick={() => setShowAllLowStock(!showAllLowStock)}
                  >
                    {showAllLowStock ? 'Show Less' : 'View All'}
                  </button>
                )}
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Products Inventory</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setSelectedProduct(null);
                  setProductData({ modelNumber: '', name: '', brand: '', price: '', warehouse1: '', warehouse2: '' });
                  setShowProductModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> Add Product
                </button>
              )}
            </div>

            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Model Number</th>
                        <th>Product Name</th>
                        <th>Description</th>
                        <th>Brand</th>
                        <th>Price</th>
                        <th>Stock Distribution</th>
                        <th>Total Stock</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td><strong>{product.modelNumber}</strong></td>
                          <td>{product.name}</td>
                          <td>{product.description || '-'}</td>
                          <td>{product.brand}</td>
                          <td>Rs. {Number(product.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                          <td>
                            {product.ProductStocks && product.ProductStocks.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {product.ProductStocks.map(stock => (
                                  stock.quantity > 0 && (
                                    <span key={stock.id} className={`badge ${stock.quantity < 10 ? 'bg-danger' : 'bg-info'} text-dark border`}>
                                      {stock.Warehouse ? stock.Warehouse.name : 'Unknown'}: {stock.quantity}
                                    </span>
                                  )
                                ))}
                                {product.ProductStocks.every(s => s.quantity === 0) && <span className="text-muted">Out of Stock</span>}
                              </div>
                            ) : (
                              <span className="text-muted">No Stock</span>
                            )}
                          </td>
                          <td><strong>{product.totalStock}</strong></td>
                          <td>
                            {canManageStock && (
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleEditProduct(product)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-sm btn-outline-danger me-2"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => {
                                setViewItem(product);
                                setViewType('product');
                                setShowViewModal(true);
                              }}
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === 'shipments' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Incoming Shipments</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setSelectedShipment(null);
                  setShipmentData({ supplier: '', date: '', warehouse: '', shipmentNo: '', items: [{ product: '', quantity: '' }] });
                  setShowShipmentModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> Record Shipment
                </button>
              )}
            </div>

            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Shipments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Shipment No</th>
                        <th>Date</th>
                        <th>Model No</th>
                        <th>Product</th>
                        <th>Supplier</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredShipments.length === 0 ? (
                        <tr><td colSpan="8" className="text-center">No shipments found</td></tr>
                      ) : (
                        filteredShipments.map(s => (
                          <tr key={s.id}>
                            <td>{s.shipmentNo}</td>
                            <td>{s.date ? new Date(s.date).toLocaleDateString() : '-'}</td>
                            <td><strong>{s.Product ? s.Product.modelNumber : '-'}</strong></td>
                            <td>{s.Product ? s.Product.name : 'Unknown Product'}</td>
                            <td>{s.supplier}</td>
                            <td>{s.location}</td>
                            <td>{s.status}</td>
                            <td>
                              {canManageStock && <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditShipment(s)}><i className="bi bi-pencil"></i></button>}
                              {canDelete && <button className="btn btn-sm btn-outline-danger me-2" onClick={() => handleDeleteShipment(s.id)}><i className="bi bi-trash"></i></button>}
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => {
                                  setViewItem(s);
                                  setViewType('shipment');
                                  setShowViewModal(true);
                                }}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === 'transfers' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Stock Transfers</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setTransferData({ product: '', from: '', to: '', quantity: '', date: '' });
                  setShowTransferModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> New Transfer
                </button>
              )}
            </div>

            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Transfers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Transfer No</th>
                        <th>Date</th>
                        <th>Model No</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransfers.length === 0 ? (
                        <tr><td colSpan="9" className="text-center">No transfers found</td></tr>
                      ) : (
                        filteredTransfers.map(t => (
                          <tr key={t.id}>
                            <td>{t.transferNo}</td>
                            <td>{t.date ? new Date(t.date).toLocaleDateString() : '-'}</td>
                            <td><strong>{t.Product ? t.Product.modelNumber : '-'}</strong></td>
                            <td>{t.fromWarehouse}</td>
                            <td>{t.toWarehouse}</td>
                            <td>{t.Product ? t.Product.name : 'Unknown Product'}</td>
                            <td>{t.quantity}</td>
                            <td>{t.status}</td>
                            <td>
                              {canManageStock && <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditTransfer(t)}><i className="bi bi-pencil"></i></button>}
                              {canDelete && <button className="btn btn-sm btn-outline-danger me-2" onClick={() => handleDeleteTransfer(t.id)}><i className="bi bi-trash"></i></button>}
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => {
                                  setViewItem(t);
                                  setViewType('transfer');
                                  setShowViewModal(true);
                                }}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === 'stockout' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Stock Out (Project Issuance)</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setSelectedStockOut(null);
                  setStockOutData({ projectId: '', product: '', quantity: '', warehouse: '' });
                  setShowStockOutModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> Issue Stock
                </button>
              )}
            </div>

            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Stock Out..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="alert alert-info">
              <i className="bi bi-info-circle"></i> Stock can only be issued to projects where Performance Security has been submitted.
            </div>
            {/* Listing for stock outs */}
            <div className="card mt-3">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Project</th>
                        <th>Model No</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>From</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStockOuts.length === 0 ? (
                        <tr><td colSpan="7" className="text-center">No stock issuance records found</td></tr>
                      ) : (
                        filteredStockOuts.map(s => (
                          <tr key={s.id}>
                            <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                            <td>{s.Project?.title || '-'}</td>
                            <td>{s.Product?.modelNumber || '-'}</td>
                            <td>{s.Product?.name}</td>
                            <td>{s.quantity}</td>
                            <td>{s.fromWarehouse || '-'}</td>
                            <td>
                              {canDelete && (
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteStockOut(s.id)}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div >
        )
      }

      {/* Add Product Modal */}
      {
        showProductModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedProduct ? 'Edit Product' : 'Add New Product'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowProductModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleAddProduct}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Model Number *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.modelNumber ? 'is-invalid' : ''}`}
                          placeholder="Enter Model Number"
                          value={productData.modelNumber}
                          onChange={(e) => setProductData({ ...productData, modelNumber: e.target.value })}
                          required
                        />
                        {errors.modelNumber && <div className="invalid-feedback">{errors.modelNumber}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Product Name *</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            required
                            value={productData.name}
                            onChange={(e) => setProductData({ ...productData, name: e.target.value })}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setProductData({ ...productData, name: cleanText(productData.name) })}
                            title="Cleanup PDF artifacts"
                          >
                            <i className="bi bi-magic"></i>
                          </button>
                        </div>
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Brand *</label>
                        <div className="input-group">
                          <select
                            className={`form-select ${errors.brand ? 'is-invalid' : ''}`}
                            value={productData.BrandId || ''}
                            onChange={(e) => {
                              const bId = e.target.value;
                              const bObj = brands.find(b => b.id.toString() === bId);
                              setProductData({
                                ...productData,
                                BrandId: bId,
                                brand: bObj ? bObj.name : ''
                              });
                            }}
                            required
                          >
                            <option value="">Select Brand</option>
                            {brands.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setShowBrandModal(true)}
                            title="Add New Brand"
                          >
                            <i className="bi bi-plus-lg"></i>
                          </button>
                          {errors.brand && <div className="invalid-feedback d-block">{errors.brand}</div>}
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Price (Rs.) *</label>
                        <input
                          type="number"
                          className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                          required
                          value={productData.price}
                          onChange={(e) => setProductData({ ...productData, price: e.target.value })}
                        />
                        {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label">Description</label>
                        <div className="input-group">
                          <textarea
                            className="form-control"
                            placeholder="Product Details / Specification"
                            rows="3"
                            value={productData.description}
                            onChange={(e) => setProductData({ ...productData, description: e.target.value })}
                          ></textarea>
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setProductData({ ...productData, description: cleanText(productData.description) })}
                            title="Cleanup PDF artifacts"
                          >
                            <i className="bi bi-magic"></i>
                          </button>
                        </div>
                      </div>



                      <div className="col-12">
                        <label className="form-label">Stock Distribution</label>
                        {stockEntries.map((entry, index) => (
                          <div className="row g-2 mb-2 align-items-center" key={index}>
                            <div className="col-7">
                              <select
                                className="form-select"
                                value={entry.warehouseId}
                                onChange={(e) => handleStockEntryChange(index, 'warehouseId', e.target.value)}
                              >
                                <option value="">Select Warehouse</option>
                                {warehouses.map(w => (
                                  <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-3">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Qty"
                                min="0"
                                value={entry.quantity}
                                onChange={(e) => handleStockEntryChange(index, 'quantity', e.target.value)}
                              />
                            </div>
                            <div className="col-2">
                              {index > 0 && (
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveStockEntry(index)}>
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={handleAddStockEntry}>
                          <i className="bi bi-plus-lg"></i> Add Warehouse
                        </button>
                      </div>
                    </div>

                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowProductModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{selectedProduct ? 'Update Product' : 'Add Product'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div >
        )
      }

      {/* Add Shipment Modal */}
      {
        showShipmentModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedShipment ? 'Edit Shipment' : 'Record Incoming Shipment'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowShipmentModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleAddShipment}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Supplier (Brand) *</label>
                      <select
                        className="form-select"
                        required
                        value={shipmentData.supplier}
                        onChange={(e) => setShipmentData({ ...shipmentData, supplier: e.target.value, items: [{ product: '', quantity: '' }] })}
                      >
                        <option value="">Select Brand/Supplier</option>
                        {brands.map(b => (
                          <option key={b.id} value={b.name}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={shipmentData.date}
                        onChange={(e) => setShipmentData({ ...shipmentData, date: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Receive At Location *</label>
                      <select
                        className="form-select"
                        required
                        value={shipmentData.warehouse}
                        onChange={(e) => setShipmentData({ ...shipmentData, warehouse: e.target.value })}
                      >
                        <option value="">Select Warehouse</option>
                        {warehouses.map(w => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    </div>

                    <h6 className="mt-4 border-bottom pb-2">Shipment Items</h6>
                    <div className="bg-light p-3 rounded">
                      {shipmentData.items && shipmentData.items.map((item, index) => (
                        <div key={index} className="row g-2 mb-3 align-items-end border-bottom pb-2">
                          <div className="col-7">
                            <label className="form-label small">Product</label>
                            <select
                              className="form-select"
                              required
                              value={item.product}
                              onChange={(e) => handleShipmentItemChange(index, 'product', e.target.value)}
                            >
                              <option value="">Select Product</option>
                              {products
                                .filter(p => !shipmentData.supplier || p.brand === shipmentData.supplier)
                                .map(p => (
                                  <option key={p.id} value={p.id}>{p.name} ({p.modelNumber})</option>
                                ))}
                            </select>
                          </div>
                          <div className="col-3">
                            <label className="form-label small">Qty</label>
                            <input
                              type="number"
                              className="form-control"
                              required
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleShipmentItemChange(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div className="col-2">
                            {shipmentData.items.length > 1 && (
                              <button type="button" className="btn btn-danger w-100" onClick={() => handleRemoveShipmentItem(index)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={handleAddShipmentItem}>
                        <i className="bi bi-plus-circle"></i> Add Another Item
                      </button>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowShipmentModal(false)}>Cancel</button>

                    <button type="submit" className="btn btn-primary">{selectedShipment ? 'Update Shipment' : 'Record Shipment'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Transfer Modal */}
      {
        showTransferModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedTransfer ? 'Update Stock Transfer' : 'New Stock Transfer'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowTransferModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleAddTransfer}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Product *</label>
                      <select
                        className="form-select"
                        required
                        value={transferData.product}
                        onChange={(e) => setTransferData({ ...transferData, product: e.target.value })}
                      >
                        <option value="">Select Product</option>
                        {products.map(p => {
                          const stockInfo = p.ProductStocks && p.ProductStocks.length > 0
                            ? p.ProductStocks.map(s => `${s.Warehouse ? s.Warehouse.name : 'Unknown'}: ${s.quantity}`).join(', ')
                            : 'No Stock';
                          return (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.modelNumber}) ({stockInfo})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {/* Selectable Model Number Field */}
                    <div className="mb-3">
                      <label className="form-label">Model Number *</label>
                      <select
                        className="form-select"
                        required
                        value={transferData.product}
                        onChange={(e) => setTransferData({ ...transferData, product: e.target.value })}
                      >
                        <option value="">Select Model Number</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.modelNumber} ({p.name})</option>
                        ))}
                      </select>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label">From Warehouse *</label>
                        <select
                          className="form-select"
                          required
                          value={transferData.fromWarehouse}
                          onChange={(e) => setTransferData({ ...transferData, fromWarehouse: e.target.value })}
                        >
                          <option value="">Select Source</option>
                          {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">To Warehouse *</label>
                        <select
                          className="form-select"
                          required
                          value={transferData.toWarehouse}
                          onChange={(e) => setTransferData({ ...transferData, toWarehouse: e.target.value })}
                        >
                          <option value="">Select Destination</option>
                          {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Quantity *</label>
                      <input
                        type="number"
                        className="form-control"
                        required
                        min="1"
                        value={transferData.quantity}
                        onChange={(e) => setTransferData({ ...transferData, quantity: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={transferData.date}
                        onChange={(e) => setTransferData({ ...transferData, date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowTransferModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{selectedTransfer ? 'Update Stock Transfer' : 'Transfer Stock'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Stock Out Modal */}
      {
        showStockOutModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedStockOut ? 'Edit Stock Issuance' : 'Issue Stock to Project'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowStockOutModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleStockOut}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Select Project *</label>
                      <select
                        className="form-select"
                        required
                        value={stockOutData.projectId}
                        onChange={(e) => setStockOutData({ ...stockOutData, projectId: e.target.value })}
                      >
                        <option value="">Select Awarded Project</option>
                        {projects
                          .filter(p => p.status === 'awarded')
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              {p.title.length > 60 ? p.title.substring(0, 60) + '...' : p.title}
                            </option>
                          ))}
                      </select>

                      <div className="form-text"> Only projects with submitted Performance Security are listed.</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Product *</label>
                      <select
                        className="form-select"
                        required
                        value={stockOutData.product}
                        onChange={(e) => setStockOutData({ ...stockOutData, product: e.target.value })}
                      >
                        <option value="">Select Product</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.modelNumber}) (Total: {p.totalStock})</option>
                        ))}
                      </select>
                    </div>
                    {/* Selectable Model Number Field */}
                    <div className="mb-3">
                      <label className="form-label">Model Number *</label>
                      <select
                        className="form-select"
                        required
                        value={stockOutData.product}
                        onChange={(e) => setStockOutData({ ...stockOutData, product: e.target.value })}
                      >
                        <option value="">Select Model Number</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.modelNumber} ({p.name})</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Quantity *</label>
                      <input
                        type="number"
                        className="form-control"
                        required
                        min="1"
                        value={stockOutData.quantity}
                        onChange={(e) => setStockOutData({ ...stockOutData, quantity: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Source Warehouse *</label>
                      <select
                        className="form-select"
                        required
                        value={stockOutData.warehouse}
                        onChange={(e) => setStockOutData({ ...stockOutData, warehouse: e.target.value })}
                      >
                        <option value="">Select Source</option>
                        {warehouses.map(w => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowStockOutModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Issue Stock</button>
                  </div>
                </form>
              </div>
            </div>
          </div >
        )
      }
      {/* View Modal */}
      {
        showViewModal && viewItem && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{viewType === 'product' ? 'Product Details' : (viewType === 'transfer' ? 'Transfer Details' : 'Shipment Details')}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  {viewType === 'product' ? (
                    <>
                      <div className="mb-3">
                        <label className="fw-bold">Model Number:</label>
                        <p>{viewItem.modelNumber}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Product Name:</label>
                        <p>{viewItem.name}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Price:</label>
                        <p>Rs. {Number(viewItem.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Description:</label>
                        <p>{viewItem.description || 'N/A'}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Stock Distribution:</label>
                        {viewItem.ProductStocks && viewItem.ProductStocks.length > 0 ? (
                          <ul className="list-group mt-1">
                            {viewItem.ProductStocks.sort((a, b) => (a.Warehouse?.name || '').localeCompare(b.Warehouse?.name || '')).map(stock => (
                              <li key={stock.id} className="list-group-item d-flex justify-content-between align-items-center py-1">
                                {stock.Warehouse ? stock.Warehouse.name : 'Unknown Warehouse'}
                                <span className={stock.quantity < 50 ? 'badge bg-danger rounded-pill' : 'badge bg-primary rounded-pill'}>
                                  {stock.quantity}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No stock available.</p>
                        )}
                      </div>

                      <div className="mt-2">
                        <label className="fw-bold">Total Stock:</label>
                        <p className="h4">{viewItem.totalStock}</p>
                      </div>
                    </>
                  ) : viewType === 'shipment' ? (
                    <>
                      <div className="mb-3">
                        <label className="fw-bold">Shipment No:</label>
                        <p>{viewItem.shipmentNo}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Date:</label>
                        <p>{viewItem.date ? new Date(viewItem.date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Supplier:</label>
                        <p>{viewItem.supplier}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Product:</label>
                        <p>{viewItem.Product ? viewItem.Product.name : 'Unknown Product'}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Location:</label>
                        <p>{viewItem.location}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Quantity:</label>
                        <p className="h4">{viewItem.quantity}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Status:</label>
                        <span className={`badge bg-${viewItem.status === 'Received' ? 'success' : 'warning'}`}>{viewItem.status}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="fw-bold">Transfer No:</label>
                        <p>{viewItem.transferNo}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Date:</label>
                        <p>{viewItem.date ? new Date(viewItem.date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">From:</label>
                        <p>{viewItem.fromWarehouse}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">To:</label>
                        <p>{viewItem.toWarehouse}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Product:</label>
                        <p>{viewItem.Product ? viewItem.Product.name : 'Unknown Product'}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Quantity:</label>
                        <p className="h4">{viewItem.quantity}</p>
                      </div>
                      <div className="mb-3">
                        <label className="fw-bold">Status:</label>
                        <span className="badge bg-success">{viewItem.status}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div >
        )
      }
      {
        activeTab === 'warehouses' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Warehouse Management</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setSelectedWarehouse(null);
                  setWarehouseData({ name: '', location: '', description: '' });
                  setShowWarehouseModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> Add Warehouse
                </button>
              )}
            </div>

            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Location</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {warehouses.sort((a, b) => a.name.localeCompare(b.name)).map(w => (
                        <tr key={w.id}>
                          <td><strong>{w.name}</strong></td>
                          <td>{w.location || '-'}</td>
                          <td>{w.description || '-'}</td>
                          <td>
                            {canManageStock && (
                              <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditWarehouse(w)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                            )}
                            {canDelete && (
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteWarehouse(w.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {warehouses.length === 0 && (
                        <tr><td colSpan="4" className="text-center">No warehouses found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>


          </div>
        )
      }

      {
        activeTab === 'brands' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Brand Management</h5>
              {canManageStock && (
                <button className="btn btn-primary" onClick={() => {
                  setSelectedBrand(null);
                  setBrandData({ name: '', description: '' });
                  setShowBrandModal(true);
                }}>
                  <i className="bi bi-plus-circle"></i> Add Brand
                </button>
              )}
            </div>

            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brands.sort((a, b) => a.name.localeCompare(b.name)).map(b => (
                        <tr key={b.id}>
                          <td><strong>{b.name}</strong></td>
                          <td>{b.description || '-'}</td>
                          <td>
                            {canManageStock && (
                              <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditBrand(b)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                            )}
                            {canDelete && (
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteBrand(b.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {brands.length === 0 && (
                        <tr><td colSpan="3" className="text-center">No brands found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Brand Modal */}
      {showBrandModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedBrand ? 'Edit Brand' : 'Add Brand'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowBrandModal(false)}><i className="bi bi-x-lg"></i></button>
              </div>
              <form onSubmit={handleSaveBrand}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={brandData.name}
                      onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={brandData.description}
                      onChange={(e) => setBrandData({ ...brandData, description: e.target.value })}
                      rows="3"
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowBrandModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Brand</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Warehouse Modal */}
      {showWarehouseModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowWarehouseModal(false)}><i className="bi bi-x-lg"></i></button>
              </div>
              <form onSubmit={handleSaveWarehouse}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={warehouseData.name}
                      onChange={(e) => setWarehouseData({ ...warehouseData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className="form-control"
                      value={warehouseData.location}
                      onChange={(e) => setWarehouseData({ ...warehouseData, location: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={warehouseData.description}
                      onChange={(e) => setWarehouseData({ ...warehouseData, description: e.target.value })}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowWarehouseModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">{selectedWarehouse ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManagement;
